import { createChart } from "lightweight-charts";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Loader2 } from "../../reusableComponents/Loader";
import { fetchCandlestickData } from "../../store/reducers/candleStickReducer";
import { Empty } from "antd";

const CandleStickChart = ({
  symbol,
  candleStickTime,
  period,
  activeTimeFrame,
  setRenderInfo,
  positions,
  setPositionInfo,
  color,
  information,
}) => {
  const chartContainerRef = useRef();
  const tooltipRef = useRef(); // tooltip element
  const { chartData: candleLightData, loading } = useSelector(
    (state) => state.candlestickChart
  );
  const { AccDetails } = useSelector((state) => state.dashboard);
  const idToken = useSelector((state) => state.ACCtoken);
  const dispatch = useDispatch();

  const [data, setData] = useState([]);
  const [chartLoading, setChartLoading] = useState(false);

  const infoNormal = information;

   const getTradeDateTime = (trade) => {
    const tradeDate = trade?.date || trade?.tradeDateTime;
    return tradeDate
  }

 const info = infoNormal?.map((item) => ({
  date: getTradeDateTime(item),  
  price: parseFloat(item.price),         
}));


  useEffect(() => {
    const updatedData = (candleLightData?.candles || [])?.map((item) => ({
      ...item,
      datetime: item?.time?.split("+")[0] + "Z",
    }));
    setData(updatedData);
  }, [candleLightData]);

  useEffect(() => {
    if (!symbol) return;

    dispatch(
      fetchCandlestickData({
        candleStickTime,
        platform: AccDetails?.platform,
        period,
        setChartLoading,
        idToken,
        activeTimeFrame,
        setRenderInfo,
        specificTimes: [],
        symbol: symbol?.split(".")?.[0]?.toLowerCase(),
        information
      })
    );
  }, [symbol, dispatch, candleStickTime, period, activeTimeFrame]);

  useEffect(() => {
    if (!data.length || chartLoading) return;

    const container = chartContainerRef.current;
    if (!container) return;

    const chart = createChart(container, {
      width: container.clientWidth,
      height: 500,
      layout: {
        background: { color: "#ffffffff" },
        textColor: "#7d7d7dff",
      },
      grid: {
        vertLines: { color: "#f2f2f2" }, // very light gray vertical lines
        horzLines: { color: "#f2f2f2" }, // very light gray horizontal lines
      },
      timeScale: {
        timeVisible: true,
        secondsVisible: false,
        borderColor: "#f0f0f0", // light border under time axis
      },
      rightPriceScale: {
        borderColor: "#f0f0f0", // light border at right side (price axis)
      },
      crosshair: {
        mode: 1,
      },
    });

    const candleSeries = chart.addCandlestickSeries({
      upColor: "#26a69a",
      downColor: "#ef5350",
      borderUpColor: "#26a69a",
      borderDownColor: "#ef5350",
      wickUpColor: "#26a69a",
      wickDownColor: "#ef5350",
    });

    const sortedData = [...data]
      .sort((a, b) => new Date(a.datetime) - new Date(b.datetime))
      .filter(
        (val, i, arr) =>
          i === 0 ||
          new Date(val.datetime).getTime() !==
          new Date(arr[i - 1].datetime).getTime()
      );

    const formattedData = sortedData.map((item) => ({
      time: Math.floor(new Date(item.datetime).getTime() / 1000),
      open: item.open,
      high: item.high,
      low: item.low,
      close: item.close,
    }));

   candleSeries.setData(
  [...formattedData].sort((a, b) => a.time - b.time)
);


    // ----- Tooltip handling -----
    const tooltip = tooltipRef.current;
    chart.subscribeCrosshairMove((param) => {
      if (!param || !param.time || param.point.x < 0 || param.point.y < 0) {
        tooltip.style.display = "none";
        return;
      }

      const price = param.seriesData.get(candleSeries);
      if (!price) return;

      const dateStr = new Date(param.time * 1000).toLocaleString();
      tooltip.style.display = "block";
      tooltip.style.left = param.point.x + 15 + "px";
      tooltip.style.top = param.point.y + 15 + "px";
      tooltip.innerHTML = `
        <div style="font-size:12px; color:white;">
         
          Open: ${price.open.toFixed(2)}<br/>
          High: ${price.high.toFixed(2)}<br/>
          Low: ${price.low.toFixed(2)}<br/>
          Close: ${price.close.toFixed(2)}
        </div>
      `;
    });

    // ----- Optional trade line/markers -----
    if (info?.length === 2) {
      const [start, end] = info;

      const trendLineSeries = chart.addLineSeries({
        color,
        lineWidth: 2,
        priceLineVisible: false,
        crossHairMarkerVisible: false,
        lineStyle: 1,
      });

      const linePoints = [
  {
    time: Math.floor(new Date(start.date + "Z").getTime() / 1000),
    value: start.price,
  },
  {
    time: Math.floor(new Date(end.date + "Z").getTime() / 1000),
    value: end.price,
  },
].sort((a, b) => a.time - b.time);

trendLineSeries?.setData(linePoints);

      trendLineSeries?.setData(linePoints);

    trendLineSeries.setMarkers(
  linePoints.map((point, idx) => ({
    time: point.time,
    position: idx === 0 ? "belowBar" : "aboveBar",
    shape: idx === 0 ? "arrowUp" : "arrowDown",
    color: idx === 0 ? "#26a69a" : "#ef5350",
  }))
);

      chart.timeScale().setVisibleRange({
        from: linePoints[0].time - 60 * 20,
        to: linePoints[1].time + 60 * 20,
      });
    }

    return () => {
      chart.remove();
    };
  }, [data, chartLoading]);

  if (data?.length <= 0 && !loading) {
    return (
      <div
        style={{
          height: "500px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Empty description="No Chart Data" />
      </div>
    );
  }

  return (
    <div style={{ position: "relative" }}>
      {chartLoading && <Loader2 open={chartLoading} />}
      <div
        ref={chartContainerRef}
        style={{
          width: "100%",
          position: "relative",
          backgroundColor: "#ffffffff",
          height: "500px",
        }}
      />
      {/* Tooltip */}
      <div
        ref={tooltipRef}
        style={{
          position: "absolute",
          display: "none",
          pointerEvents: "none",
          background: "rgba(0, 0, 0, 0.7)",
          padding: "6px",
          borderRadius: "4px",
          color: "#fff",
          fontSize: "12px",
          zIndex: 10,
        }}
      />
    </div>
  );
};

export default CandleStickChart;
